import { useState, useRef, useEffect } from 'react';
import { Map, Clock, Star, Check, ChevronDown, ChevronUp, BookmarkMinus, ThumbsUp, ThumbsDown, Share2 } from 'lucide-react';
import type { RankedPlace } from '@/hooks/useLLM';
import { formatRating, formatOpeningHours } from '@/utils/formatters';
import { inferOpenStatus } from '@/utils/openingHoursInference';
import { useLanguage } from '@/hooks/useLanguage';
import { placePhotoUrl, staticMapUrl } from '@/utils/googleProxy';
import { useAuth } from '@/hooks/useAuth';
import { getLocalSignal, setTasteSignal, recordTasteEvent } from '@/lib/tasteGraph';

const VIBE_CONFIG: Record<string, { emoji: string; label: string }> = {
  hungry: { emoji: '🍜', label: 'Hungry' },
  adventurous: { emoji: '⚽', label: 'Adventurous' },
  'must-sees': { emoji: '📸', label: 'Must-See' },
  'feeling-lucky': { emoji: '🎲', label: 'Feeling Lucky' },
  popular: { emoji: '🔥', label: 'Popular' },
  'hidden-gems': { emoji: '💎', label: 'Hidden Gem' }
};

interface POICardProps {
  place: RankedPlace;
  transport: string;
  timezone?: string;
  referralCode?: string | null;
  isSelected: boolean;
  isSaved?: boolean;
  onToggleSelect: (placeId: string) => void;
  isExpanded: boolean;
  onToggleExpand: (placeId: string) => void;
}

export function POICard({ place, transport, timezone, referralCode, isSelected, isSaved = false, onToggleSelect, isExpanded, onToggleExpand }: POICardProps) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [showToast, setShowToast] = useState(false);
  // Soft preference signal — localStorage for anon, Supabase for logged-in users
  const [signal, setSignal] = useState<'like' | 'dislike' | null>(() => getLocalSignal(place.id));
  const [signalToast, setSignalToast] = useState<string | null>(null);
  const signalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (signalTimerRef.current) clearTimeout(signalTimerRef.current); }, []);

  // Lazy photo loading: don't set <img src> until card is near the viewport
  const hasPhoto = place.photos && place.photos.length > 0;
  const [photoVisible, setPhotoVisible] = useState(false);
  const [photoLoaded, setPhotoLoaded] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [photoFailed, setPhotoFailed] = useState(false);
  const photoContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasPhoto) return;
    const el = photoContainerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setPhotoVisible(true); observer.disconnect(); } },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasPhoto]);

  const handleSignal = (e: React.MouseEvent, type: 'like' | 'dislike') => {
    e.stopPropagation();
    const userId = user?.id ?? null;
    const newSignal = signal === type ? null : type;
    setSignal(newSignal);
    setTasteSignal(userId, place.id, newSignal);

    if (signalTimerRef.current) clearTimeout(signalTimerRef.current);

    if (newSignal !== null) {
      recordTasteEvent(userId, place.id, type, {
        name: place.name, types: place.types, category: place.category, matchingVibes: place.matchingVibes,
      });
      setSignalToast(type === 'like' ? 'More like this ✓' : 'Less like this ✓');
    } else {
      recordTasteEvent(userId, place.id, type === 'like' ? 'unlike' : 'undislike', {
        name: place.name, types: place.types,
      });
      setSignalToast('Cleared');
    }
    signalTimerRef.current = setTimeout(() => setSignalToast(null), 2000);
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const name = place.displayName?.text || place.name || '';
    const rating = place.rating ? `★ ${formatRating(place.rating)}` : '';
    const hook = place.hook || '';
    const coordsQuery = place.location ? `${place.location.lat},${place.location.lng}` : undefined;
    const mapsUrl = coordsQuery
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coordsQuery)}&query_place_id=${encodeURIComponent(place.id)}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name} ${place.formattedAddress}`)}`;
    const text = [
      `${name} ${rating}`.trim(),
      hook,
      place.formattedAddress,
      mapsUrl,
      `\n📍 Found on TripBoom, your AI travel concierge — ${referralCode ? `tripboom.app?ref=${referralCode}` : 'tripboom.app'}`
    ].filter(Boolean).join('\n');

    if (navigator.share) {
      try {
        await navigator.share({ title: name, text });
      } catch {
        // User cancelled or share failed — fall through to clipboard
      }
    } else {
      await navigator.clipboard.writeText(text);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
    }

    // Record share as a taste signal
    if (user?.id) {
      recordTasteEvent(user.id, place.id, 'share', {
        name: place.displayName?.text || '',
        types: place.types || [],
        category: place.category || '',
        matchingVibes: place.matchingVibes || []
      });
    }
  };

  const handleNavigate = () => {
    const travelMode = transport === 'walking' ? 'walking' : transport === 'transit' ? 'transit' : 'driving';
    // Use coordinates as destination to avoid address-text geocoding ambiguity (wrong-place bug).
    // Add destination_place_id so Maps shows the correct place name and info panel.
    const destination = `${place.location.lat},${place.location.lng}`;
    const placeId = encodeURIComponent(place.id);
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${destination}&destination_place_id=${placeId}&travelmode=${travelMode}`,
      '_blank'
    );
  };

  // weekdayDescriptions is Mon-Sun (0-6), JS getDay() is Sun(0)-Sat(6)
  const getDestinationDay = () => {
    let jsDay: number;
    if (timezone) {
      const now = new Date();
      const dayInTimezone = new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        timeZone: timezone
      }).format(now);
      const dayMap: Record<string, number> = {
        'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6
      };
      jsDay = dayMap[dayInTimezone] ?? new Date().getDay();
    } else {
      jsDay = new Date().getDay();
    }
    return jsDay === 0 ? 6 : jsDay - 1;
  };

  const getOpenStatus = () => {
    const currentHour = new Date().getHours();
    const hours = place.currentOpeningHours;
    const inferred = inferOpenStatus(hours, place.types || [], currentHour);
    // Use real schedule text when available, otherwise use inferred label
    const scheduleText = hours ? formatOpeningHours(hours, timezone) : null;
    const hasRealSchedule = scheduleText && scheduleText !== 'Hours unavailable';
    const status = hasRealSchedule
      ? scheduleText
      : inferred.confidence === 'known'
        ? (inferred.likelyOpen ? 'Open' : 'Closed')
        : inferred.confidence === 'inferred'
          ? (inferred.likelyOpen ? 'Likely open' : 'Likely closed')
          : null;  // unknown — hide badge
    if (!status) return null;
    return { status, isOpen: inferred.likelyOpen };
  };

  const openStatus = getOpenStatus();
  const categoryEmoji = place.category.toLowerCase().includes('food') || place.category.toLowerCase().includes('restaurant') ? '🍽️'
    : place.category.toLowerCase().includes('bar') ? '🍺'
    : place.category.toLowerCase().includes('museum') ? '🏛️'
    : place.category.toLowerCase().includes('park') ? '🌳'
    : '📍';

  return (
    <>
      <div className="glass-morphism rounded-2xl overflow-hidden card-hover cursor-pointer" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleExpand(place.id); } }} onClick={() => onToggleExpand(place.id)}>
        <div
          ref={photoContainerRef}
          className="relative w-full"
          style={{ height: (hasPhoto && !photoFailed) ? '160px' : '80px' }}
        >
          {hasPhoto && !photoFailed ? (
            <>
              {/* Skeleton shimmer — visible until photo loads */}
              <div
                className={`absolute inset-0 bg-gradient-to-r from-[var(--bg-elevated)] via-[var(--bg-secondary)] to-[var(--bg-elevated)] bg-[length:200%_100%] animate-[shimmer_1.5s_ease-in-out_infinite] transition-opacity duration-300 ${photoLoaded ? 'opacity-0' : 'opacity-100'}`}
              />
              {photoVisible && (
                <img
                  src={placePhotoUrl(place.photos![photoIndex])}
                  alt={place.name}
                  className={`w-full h-full object-cover transition-opacity duration-300 ${photoLoaded ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={() => setPhotoLoaded(true)}
                  onError={() => {
                    // Try next photo in array, or fall back to emoji
                    const nextIndex = photoIndex + 1;
                    if (place.photos && nextIndex < place.photos.length) {
                      setPhotoIndex(nextIndex);
                    } else {
                      setPhotoFailed(true);
                    }
                  }}
                />
              )}
            </>
          ) : (
            <div className="w-full h-full bg-[var(--bg-elevated)] flex items-center justify-center text-4xl">
              {categoryEmoji}
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          <div className="absolute bottom-0 left-0 right-0 p-3">
            <div className="flex items-end justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg text-white leading-tight drop-shadow">
                  {place.name}
                </h3>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-[var(--accent-teal)]/80 text-white text-xs font-medium backdrop-blur-sm">
                    {place.category}
                  </span>
                  {place.matchingVibes && (
                    <>
                      {(Array.isArray(place.matchingVibes) ? place.matchingVibes : [place.matchingVibes]).map(vibe => {
                        const isCustom = vibe.startsWith('custom:');
                        const config = VIBE_CONFIG[vibe];
                        if (!config && !isCustom) return null;
                        return isCustom ? (
                          <span
                            key={vibe}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/70 text-white text-xs font-medium backdrop-blur-sm"
                            title={vibe.slice(7)}
                          >
                            <span>✨</span>
                            <span>{vibe.slice(7)}</span>
                          </span>
                        ) : (
                          <span key={vibe} className="text-sm drop-shadow" title={config!.label}>
                            {config!.emoji}
                          </span>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {place.rating && (
                  <div className="flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-2 py-0.5">
                    <Star className="w-3.5 h-3.5 text-[var(--accent-gold)] fill-current" />
                    <span className="text-sm text-white font-medium">{formatRating(place.rating)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="absolute top-2 right-2">
            <div className="w-6 h-6 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
              {isExpanded
                ? <ChevronUp className="w-3.5 h-3.5 text-white" />
                : <ChevronDown className="w-3.5 h-3.5 text-white" />
              }
            </div>
          </div>

          <div className="absolute top-2 left-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleSelect(place.id);
              }}
              className={`w-10 h-10 rounded-xl border-2 flex items-center justify-center flex-shrink-0 transition-all focus:outline-none focus:ring-2 focus:ring-[var(--accent-teal)]/40 focus:ring-offset-2 focus:ring-offset-[var(--bg-primary)] ${
                isSaved
                  ? 'bg-[var(--accent-teal)] text-white border-[var(--accent-teal)] shadow-[0_0_20px_rgba(20,184,166,0.5)] animate-none'
                  : isSelected
                  ? 'bg-[var(--accent-gold)] text-black border-[var(--accent-gold)] shadow-[0_0_25px_rgba(255,184,0,0.65)] animate-none'
                  : 'border-[3px] border-[#FDE047] bg-black/70 text-[#FDE047] shadow-[0_0_22px_rgba(253,224,71,0.45)] animate-throb'
              }`}
              aria-label={isSaved ? 'Unsave' : t.results.iWannaGo}
              title={isSaved ? 'Unsave' : t.results.iWannaGo}
            >
              {isSaved ? <BookmarkMinus className="w-5 h-5 text-white" /> : isSelected ? <Check className="w-5 h-5 text-black" /> : null}
            </button>
          </div>
        </div>

        <div className="p-3 space-y-2">
          <p className={`text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-line ${isExpanded ? '' : 'line-clamp-2'}`}>
            {place.hook}
          </p>
          <div className="flex items-center justify-between">
            <button
              onClick={(e) => { e.stopPropagation(); onToggleExpand(place.id); }}
              className="text-xs text-[var(--accent-teal)] hover:underline"
              aria-label={isExpanded ? t.common.showLess : t.common.showMore}
            >
              {isExpanded ? t.common.showLess : t.common.showMore}
            </button>
            <div className="relative flex items-center gap-1">
              {signalToast && (
                <span className="absolute bottom-full right-0 mb-1.5 px-2 py-1 rounded-md bg-[var(--bg-elevated)] border border-[var(--border)] text-xs whitespace-nowrap animate-fade-in z-10 text-[var(--text-secondary)]">
                  {signalToast}
                </span>
              )}
              <button
                onClick={(e) => handleSignal(e, 'like')}
                title={t.poi.moreLikeThis}
                aria-label={t.poi.moreLikeThis}
                aria-pressed={signal === 'like'}
                className={`p-1.5 rounded-lg transition-colors ${
                  signal === 'like'
                    ? 'text-[var(--accent-teal)] bg-[var(--accent-teal)]/10'
                    : 'text-[var(--text-secondary)]/40 hover:text-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/10'
                }`}
              >
                <ThumbsUp className="w-3.5 h-3.5" style={signal === 'like' ? { fill: 'currentColor' } : undefined} />
              </button>
              <button
                onClick={(e) => handleSignal(e, 'dislike')}
                title={t.poi.lessLikeThis}
                aria-label={t.poi.lessLikeThis}
                aria-pressed={signal === 'dislike'}
                className={`p-1.5 rounded-lg transition-colors ${
                  signal === 'dislike'
                    ? 'text-[var(--status-amber)] bg-[var(--status-amber-bg)]'
                    : 'text-[var(--text-secondary)]/40 hover:text-[var(--status-amber)] hover:bg-[var(--status-amber-bg)]'
                }`}
              >
                <ThumbsDown className="w-3.5 h-3.5" style={signal === 'dislike' ? { fill: 'currentColor' } : undefined} />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap text-xs text-[var(--text-secondary)]">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {place.estimatedDuration}
            </span>
            <span title={place.travelTimeIsEstimated ? 'Rough estimate based on distance — actual travel time may vary' : 'Travel time from Google Maps'}>
              {place.realTravelText ? `${place.realTravelText} ${transport}` : place.distanceNote}
              {place.travelTimeIsEstimated && <span className="text-[var(--status-amber)] ml-1">(rough est.)</span>}
            </span>
            {place.practicalityRank !== undefined && (
              <span
                className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                  place.practicalityRank >= 8
                    ? 'bg-[var(--status-green-bg)] text-[var(--status-green)]'
                    : place.practicalityRank >= 5
                    ? 'bg-[var(--status-amber-bg)] text-[var(--status-amber)]'
                    : 'bg-[var(--status-red-bg)] text-[var(--status-red)]'
                }`}
                title={`Practicality score: ${place.practicalityRank}/10`}
              >
                {place.practicalityRank >= 8 ? 'Easy' : place.practicalityRank >= 5 ? 'Doable' : 'Far'}
              </span>
            )}
            {openStatus && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${openStatus.isOpen ? 'bg-[var(--status-green-bg)] text-[var(--status-green)]' : 'bg-[var(--status-amber-bg)] text-[var(--status-amber)]'}`}>
                {openStatus.status}
              </span>
            )}
          </div>

          {!isExpanded && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={(e) => { e.stopPropagation(); handleNavigate(); }}
                className="flex-1 py-2 px-3 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-teal)]/20 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-teal)] transition-colors flex items-center justify-center gap-1"
                aria-label={`${t.common.navigate} to ${place.name}`}
              >
                <Map className="w-4 h-4" />
                {t.common.navigate}
              </button>
              <button
                onClick={handleShare}
                className="flex-1 py-2 px-3 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-teal)]/20 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-teal)] transition-colors flex items-center justify-center gap-1"
                aria-label={`${t.common.share} ${place.name}`}
              >
                <Share2 className="w-4 h-4" />
                {t.common.share}
              </button>
            </div>
          )}
        </div>

        {isExpanded && (
          <div className="border-t border-[var(--border)] p-3 space-y-3">
            <div className="w-full h-48 rounded-lg overflow-hidden">
              <img
                src={staticMapUrl(place.location.lat, place.location.lng)}
                alt="Map"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-2">
              <p className="text-sm text-[var(--text-secondary)]">
                <span className="font-medium text-[var(--text-primary)]">{t.results.address}:</span> {place.formattedAddress}
              </p>

              {place.currentOpeningHours && place.currentOpeningHours.weekdayDescriptions && (
                <div className="text-sm text-[var(--text-secondary)]">
                  <span className="font-medium text-[var(--text-primary)]">{t.results.hoursToday}:</span>
                  <div className="mt-1">
                    {place.currentOpeningHours.weekdayDescriptions[getDestinationDay()]}
                  </div>
                </div>
              )}

              {place.websiteUri && (
                <a
                  href={place.websiteUri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[var(--accent-teal)] hover:underline inline-block"
                >
                  {t.results.visitWebsite} →
                </a>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); handleNavigate(); }}
                className="flex-1 py-2 px-3 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-teal)]/20 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-teal)] transition-colors flex items-center justify-center gap-1"
                aria-label={`${t.common.navigate} to ${place.name}`}
              >
                <Map className="w-4 h-4" />
                {t.common.navigate}
              </button>
              <button
                onClick={handleShare}
                className="flex-1 py-2 px-3 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-teal)]/20 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-teal)] transition-colors flex items-center justify-center gap-1"
                aria-label={`${t.common.share} ${place.name}`}
              >
                <Share2 className="w-4 h-4" />
                {t.common.share}
              </button>
            </div>
          </div>
        )}
      </div>

      {showToast && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg px-4 py-3 shadow-lg z-50 animate-fade-in">
          <p className="text-sm text-[var(--text-primary)]">{t.common.copiedToClipboard}</p>
        </div>
      )}
    </>
  );
}
